"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Fade,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { bookletRequestDecision } from "../../Redux/Slices/Booklet/Admin/BookletRequest/BookletRequestDecision";
import { bookletRequestRead } from "../../Redux/Slices/Booklet/Admin/BookletRequest/BookletRequestRead";
import { BookletCartItem } from "../../Booklet/pages/My_request_Details/IMy_request_Details";
import CartCommanTable, {
  type CartTableColumn,
} from "../../Booklet/components/CommonTable/CartCommanTable";
import PrintIcon from "@mui/icons-material/Print";
import { PrinterOptions } from "../../Redux/Slices/Booklet/Tutor/PrinterOptions/PrinterOptions";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { studyMaterialRequestCreate } from "../../Redux/Slices/Booklet/Tutor/StudyMaterial/StudyMaterialRequestCreate";
import ConfirmDialog from "../../Booklet/components/ComformDialog/ConformDialog";
import { PrinterChange } from "../../Redux/Slices/Booklet/Tutor/PrinterOptions/PrinterChange";
import { PrinterList } from "../../Redux/Slices/Booklet/Tutor/PrinterOptions/Printlist";
import { DefaultPrinter } from "../../Redux/Slices/Booklet/Tutor/PrinterOptions/DefaultPrinter";
import { DefaultPrinterUpdate } from "../../Redux/Slices/Booklet/Tutor/PrinterOptions/DefaultPrinterChange";
import { Grid } from "@mui/material";
import { AdminReprint } from "../../Redux/Slices/Booklet/Admin/AdminReprint/AdminReprint";
import { Table } from "lucide-react";
import { DashboardPrintlist } from "../../Redux/Slices/Booklet/Tutor/PrinterOptions/DashboardPrintlist";

interface PrintForm {
  paperSize: string;
  side: string;
  staplePosition: string | null;
  color: string;
  orientation: string;
  scale: string;
  stapler: string;
  tutor_remarks: String;
  pages_per_sheet: string;
}

const BookletRequestDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);

  const { id } = (location.state as { id: number }) || {};
  const { data, isLoading } = useAppSelector(
    (state) => state.BookletRequestReadSlice
  );

  const { data: printeroptions } = useAppSelector(
    (state) => state.PrinterOptionsSlice
  );
  const { data: printerListData } = useAppSelector(
    (state) => state.DashbnoardPrintList
  );
  const { data: Defaultprinteroptions } = useAppSelector(
    (state) => state.DefaultPrinterOptionsUpdate
  );

  const { isLoading: reprintLoading } = useAppSelector(
    (state) => state.TutorAdminReprint
  );

  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [reprintSuccess, setReprintSuccess] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMsg, setDialogMsg] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const tutorRemarks = data?.data?.tutor_remarks ?? "";
  const [openReprintDialog, setOpenReprintDialog] = useState(false);
  const [reprintCart, setReprintCart] = useState<any[]>([]);
  const [editPrinter, setEditPrinter] = useState(false);

  const [printForm, setPrintForm] = useState<PrintForm>({
    paperSize: "",
    side: "",
    staplePosition: null,
    color: "",
    orientation: "",
    scale: "",
    stapler: "NO",
    tutor_remarks: "",
    pages_per_sheet: "",
  });

  const selectedCenter = data?.data?.center_details?.[0]?.center_id || "";
  const handlePrintChange = (field: keyof PrintForm, value: string) => {
    setPrintForm((prev) => ({ ...prev, [field]: value }));
  };

  const options = printeroptions?.data ?? [];
  const optionsByCategory: Record<string, any[]> = Array.isArray(options)
    ? options.reduce((acc: Record<string, any[]>, option: any) => {
        if (!acc[option.category_id]) acc[option.category_id] = [];
        acc[option.category_id].push(option);
        return acc;
      }, {})
    : {};

  useEffect(() => {
    if (id) {
      dispatch(bookletRequestRead({ request_id: id }));
      dispatch(PrinterOptions());
      // dispatch(PrinterList());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedCenter) {
      dispatch(DashboardPrintlist({ center_id: selectedCenter }));
    }
  }, [data, selectedCenter]);

  const mapCartItems = (items: any[]): BookletCartItem[] => {
    return items.map((item) => {
      const driveData = item.drive_data;
      const parents = driveData?.parents || [];

      // Use first subject name if multiple
      const subjectName = item.subject_data?.[0]?.name || "-";

      const classGroup =
        item.subject_data?.[0]?.yeargroup_data.year_group || "-";

      return {
        cart_id: item.cart_id,
        subject: subjectName || "-",
        classGroup: classGroup || "-", // Use parent folder names as class/group
        topic: driveData?.name || "-", // Use file name as topic
        Previewid: driveData?.id || "-",
        fileType: driveData?.name || "-",
        lastUploadDate: driveData?.modifiedTime
          ? new Date(driveData.modifiedTime).toLocaleDateString()
          : "-",
        requestQty: item.quantity ?? 0,
      };
    });
  };
  const cartItems = mapCartItems(data?.data?.cart_items ?? []);

  // ---------- Table Columns ----------
  const columns: CartTableColumn<BookletCartItem>[] = [
    { key: "classGroup", title: "Class/Group" },
    { key: "subject", title: "Subject" },
    // { key: "topic", title: "Topic" },
    {
      key: "fileType",
      title: "File",
    },
    { key: "lastUploadDate", title: "Last Upload Date" },
    { key: "requestQty", title: "Request Qty", align: "right" },
  ];

  const totalQty = cartItems.reduce(
    (sum, row) => sum + (row.requestQty || 0),
    0
  );

  useEffect(() => {
    if (data?.data?.printer_details?.[0]?.printer_id) {
      setSelectedPrinter(data.data.printer_details[0].printer_id);
    }
  }, [data]);

  const handlePrinterChange = async () => {
    if (!selectedPrinter) return;
    dispatch(DefaultPrinterUpdate({ id: selectedPrinter }));
    setIsEditing(true);
    // const payload = {
    //   request_id: data?.data?.request_id,
    //   printer_id: selectedPrinter,
    //   tutor_id: data?.data?.tutor_details?.id,
    // };

    // try {
    //   const res = await dispatch(PrinterChange(payload));

    //   // Assuming your slice sets isSuccess or similar
    //   if (res?.payload) {
    //     // 🔹 Fetch printer options again
    //     await dispatch(DefaultPrinterUpdate({ id: selectedPrinter }));
    //     // 🔹 Enable edit mode for print form
    //     setIsEditing(true);
    //   }
    // } catch (err) {
    //   console.error("Printer change failed:", err);
    // }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setIsSaved(false);
  };

  // ---------- Submit handler ----------
  const handleDecision = async (status: "approved" | "rejected") => {
    try {
      await dispatch(
        bookletRequestDecision({
          admin_remarks: remarks,
          approval_status: status,
          request_id: id,
        })
      );

      setDialogMsg(
        status === "approved"
          ? "Request has been approved successfully!"
          : "Request has been rejected successfully!"
      );
      setDialogOpen(true);

      setTimeout(() => {
        setDialogOpen(false);
        navigate(-1);
      }, 2000);
    } catch (err) {
      console.error("Decision failed:", err);
    }
  };

  useEffect(() => {
    if (Defaultprinteroptions?.data && isEditing) {
      const defaults = Defaultprinteroptions.data;
      setPrintForm((prev) => ({
        ...prev,
        paperSize: defaults.papper_size || prev.paperSize,
        side: defaults.side || prev.side,
        orientation: defaults.page_orientation || prev.orientation,
        scale: defaults.scale || prev.scale,
        stapler: defaults.stapler === true ? "YES" : "NO",
        staplePosition: defaults.stapler_position || prev.staplePosition,
        color: defaults.color || prev.color,
        pages_per_sheet: defaults.pages_per_sheet || prev.pages_per_sheet,
      }));
    }
  }, [Defaultprinteroptions, isEditing]);

  useEffect(() => {
    if (data?.data) {
      const cartItem = data.data;
      setPrintForm({
        paperSize: cartItem.papper_size?.data?.po_id || "",
        side: cartItem.side?.data?.po_id || "",
        staplePosition: cartItem.stapler_position?.data?.po_id || null,
        color: cartItem.color?.data?.po_id || "",
        orientation: cartItem.page_orientation?.data?.po_id || "",
        scale: cartItem.scale?.data?.po_id || "",
        stapler:
          cartItem.stapler?.data === true || cartItem.stapler?.data === "true"
            ? "YES"
            : "NO",
        tutor_remarks: tutorRemarks || "",
        pages_per_sheet: cartItem.pages_per_sheet?.data?.po_id || "",
      });
      setIsSaved(false);
    }
  }, [data]);

  const handleUpdate = async () => {
    const payload = {
      request_id: data?.data.request_id,
      status: true,
      tutor_remarks: printForm.tutor_remarks,
      cart_ids: cartItems.map((item) => item.cart_id),
      papper_size: printForm.paperSize,
      side: printForm.side,
      page_orientation: printForm.orientation,
      scale: printForm.scale,
      stapler: printForm.stapler === "YES",
      stapler_position: printForm.staplePosition || null,
      color: printForm.color,
      pages_per_sheet: printForm.pages_per_sheet,
    };

    const res = await dispatch(studyMaterialRequestCreate(payload));

    if (res?.payload?.status === true) {
      // 🔹 Printer options saved — allow approval again
      setIsEditing(false);
      setIsSaved(true);
    }
  };

  const isChanged = (category: string) => {
    switch (category) {
      case "paperSize":
        return data?.data?.papper_size?.changed;
      case "side":
        return data?.data?.side?.changed;
      case "color":
        return data?.data?.color?.changed;
      case "orientation":
        return data?.data?.page_orientation?.changed;
      case "scale":
        return data?.data?.scale?.changed;
      case "stapler":
        return data?.data?.stapler?.changed;
      case "staplePosition":
        return data?.data?.stapler_position?.changed;
      default:
        return false;
    }
  };

  useEffect(() => {
    if (data?.data?.admin_remarks) {
      setRemarks(data.data.admin_remarks);
    }
  }, [data]);

  const handleReprint = () => {
    const formatted = cartItems.map((item) => ({
      cart_id: item.cart_id,
      subject: item.subject,
      classGroup: item.classGroup,
      topic: item.topic,
      File: item.fileType,
      lastUploadDate: item.lastUploadDate,
      quantity: 1,
    }));
    setReprintCart(formatted);
    setOpenReprintDialog(true);
  };

  const handleQtyChange = (cart_id: string, amount: number) => {
    setReprintCart((prev) => {
      const updated = prev
        .map((item) =>
          item.cart_id === cart_id
            ? { ...item, quantity: Math.max(0, item.quantity + amount) }
            : item
        )
        .filter((item) => item.quantity > 0); // 🔥 Remove zero qty rows

      return updated;
    });
  };

  const isSubmitDisabled = reprintCart.length === 0;

  const handleReprintSubmit = async () => {
    const payload = {
      request_id: data?.data?.request_id,
      cart_data: reprintCart.map((item) => ({
        cart_id: item.cart_id,
        quantity: item.quantity,
      })),
    };

    const res = await dispatch(AdminReprint(payload));
    console.log(res, "respponse");

    // if (res?.payload?.status === true) {
    setOpenReprintDialog(false);
    setDialogMsg("Reprint has been submitted successfully!");
    setReprintSuccess(true);
    //   }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "0px 24px", mb: 3 }}>
      {/* ---------- Tutor Details ---------- */}
      <Box>
        <Button
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            textTransform: "none",
            color: "black",
          }}
          onClick={() => navigate("/booklet_request")}
        >
          <ArrowBackIcon />
          Back to Booklet Request
        </Button>
      </Box>
      <Box sx={{ p: 2, mb: 3 }}>
        <Typography fontSize={"20px"} fontWeight="500">
          Detail of Tutor
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Box
            display="grid"
            gridTemplateColumns="repeat(4, 1fr)"
            gap={2}
            mt={2}
          >
            <Box>
              <Typography fontWeight={500} fontSize={"14px"}>
                Center
              </Typography>
              <Typography
                fontSize={"14px"}
                color="text.secondary"
                marginTop={"6px"}
              >
                {data?.data?.center_details?.[0]?.center_name ?? "N/A"}
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={"14px"} fontWeight={500}>
                Tutor Name
              </Typography>
              <Typography
                color="text.secondary"
                fontSize={"14px"}
                marginTop={"6px"}
              >
                {data?.data?.tutor_details?.name ?? "N/A"}
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={"14px"} fontWeight={500}>
                Request Date
              </Typography>
              <Typography
                color="text.secondary"
                fontSize={"14px"}
                marginTop={"6px"}
              >
                {data?.data?.created_at
                  ? new Date(data.data.created_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Box>

            <Box>
              <Typography fontSize={"14px"} fontWeight={500}>
                Request Status
              </Typography>
              <Typography fontWeight={500} color="primary" marginTop={"6px"}>
                {data?.data?.approval_status === "approved"
                  ? "Request approved"
                  : data?.data?.approval_status === "pending"
                  ? "Waiting for Approval"
                  : "Request Rejected"}
              </Typography>
            </Box>
          </Box>
          {/* ---------- Printer Selection ---------- */}
        </Box>
      </Box>

      {/* ---------- Booklet Details ---------- */}
      <Box p={2}>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography fontSize={"20px"} fontWeight="500" mb={2}>
            Details of Booklet
          </Typography>
          {data?.data?.approval_status !== "pending" && (
            <Button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#009ddf",
                color: "white",
                textTransform: "none",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: 500,
                marginBottom: "10px",
                fontSize: "14px",
              }}
              onClick={handleReprint}
            >
              <PrintIcon style={{ fontSize: "18px" }} />
              Reprint
            </Button>
          )}
        </Grid>

        <CartCommanTable<BookletCartItem>
          loading={isLoading}
          columns={columns}
          data={cartItems}
          pageType="details"
        />
      </Box>
      <Box sx={{ mt: 3, mb: 1, p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          Printer Selection
          {data?.data?.approval_status === "pending" && (
            <Grid item xs={4} display="flex" gap={1}>
              {/* EDIT BUTTON (Shows in View Mode) */}
              {!editPrinter && (
                <Button
                  startIcon={<EditIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "20px",
                    fontSize: "13px",
                    minWidth: "auto",
                    px: 2,
                  }}
                  variant="outlined"
                  onClick={() => setEditPrinter(true)}
                >
                  Edit
                </Button>
              )}

              {/* SAVE + CANCEL BUTTONS (Show only in Edit Mode) */}
              {editPrinter && (
                <>
                  <Button
                    startIcon={<SaveIcon />}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#009DFF",
                      color: "#ffff",
                      borderRadius: "20px",
                      fontSize: "13px",
                      minWidth: "auto",
                      px: 2,
                      "&:hover": {
                        opacity: 0.8,
                        bgcolor: "#009DFF",
                      },
                    }}
                    variant="contained"
                    disabled={!selectedPrinter}
                    onClick={() => {
                      handlePrinterChange();
                      setEditPrinter(false);
                    }}
                  >
                    Save
                  </Button>

                  <Button
                    sx={{
                      textTransform: "none",
                      borderRadius: "20px",
                      fontSize: "13px",
                      minWidth: "auto",
                      px: 2,
                      border: "1px solid #ccc",
                    }}
                    variant="outlined"
                    onClick={() => setEditPrinter(false)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Grid>
          )}
        </Typography>

        <FormControl fullWidth size="small" sx={{ maxWidth: "500px", mt: 1 }}>
          <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
            Select Printer
          </FormLabel>
          <Select
            value={selectedPrinter}
            onChange={(e) => setSelectedPrinter(e.target.value)}
            disabled={data?.data?.approval_status !== "pending" || !editPrinter}
          >
            {printerListData?.data?.map((printer: any) => (
              <MenuItem key={printer.printer_id} value={printer.printer_id}>
                {printer.printer_details?.name} (
                {printer.printer_details?.model})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 3, mb: 2, p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          Printing Format Details
          {data?.data?.approval_status === "pending" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {isEditing ? (
                <>
                  <Button
                    onClick={handleUpdate}
                    startIcon={<SaveIcon />}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#009DFF",
                      color: "#ffff",
                      borderRadius: "20px",
                      fontSize: "13px",
                      minWidth: "auto",
                      px: 2,
                      "&:hover": {
                        opacity: 0.8,
                        bgcolor: "#009DFF",
                      },
                    }}
                    variant="contained"
                  >
                    Save
                  </Button>

                  <Button
                    onClick={() => {
                      // 🔹 Revert back to original request data (not defaults)
                      const cartItem = data?.data;
                      setPrintForm({
                        paperSize: cartItem?.papper_size?.data?.po_id || "",
                        side: cartItem?.side?.data?.po_id || "",
                        staplePosition:
                          cartItem?.stapler_position?.data?.po_id || null,
                        color: cartItem?.color?.data?.po_id || "",
                        orientation:
                          cartItem?.page_orientation?.data?.po_id || "",
                        scale: cartItem?.scale?.data?.po_id || "",
                        stapler:
                          cartItem?.stapler?.data === true ||
                          cartItem?.stapler?.data === "true"
                            ? "YES"
                            : "NO",
                        tutor_remarks: cartItem?.tutor_remarks || "",
                        pages_per_sheet:
                          cartItem?.pages_per_sheet?.data?.po_id || "",
                      });
                      setIsEditing(false);
                    }}
                    sx={{
                      textTransform: "none",
                      borderRadius: "20px",
                      fontSize: "13px",
                      minWidth: "auto",
                      px: 2,
                      border: "1px solid #ccc",
                    }}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEditClick}
                  startIcon={<EditIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "20px",
                    fontSize: "13px",
                    minWidth: "auto",
                    px: 2,
                  }}
                  variant="outlined"
                >
                  Edit
                </Button>
              )}
            </Box>
          )}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(auto-fit, minmax(165px, 1fr))",
            },
            gap: { xs: "20px", md: "30px 40px" },
            width: "100%",
            maxWidth: "1060px",
            mt: 2,
          }}
        >
          {/* Paper Size */}
          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Paper Size
            </FormLabel>
            <Select
              value={printForm.paperSize}
              disabled={!isEditing}
              onChange={(e) => handlePrintChange("paperSize", e.target.value)}
            >
              {optionsByCategory["CAT001"]?.map((opt) => (
                <MenuItem key={opt.po_id} value={opt.po_id}>
                  {opt.printer_option_value}
                </MenuItem>
              ))}
            </Select>

            {isChanged("paperSize") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          {/* Side */}
          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Side
            </FormLabel>
            <Select
              value={printForm.side}
              disabled={!isEditing}
              onChange={(e) => handlePrintChange("side", e.target.value)}
            >
              {optionsByCategory["CAT002"]?.map((opt) => (
                <MenuItem key={opt.po_id} value={opt.po_id}>
                  {opt.printer_option_value}
                </MenuItem>
              ))}
            </Select>
            {isChanged("side") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          {/* Color */}
          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Black/Color
            </FormLabel>
            <Select
              value={printForm.color}
              disabled={!isEditing}
              onChange={(e) => handlePrintChange("color", e.target.value)}
            >
              {optionsByCategory["CAT007"]?.map((opt) => (
                <MenuItem key={opt.po_id} value={opt.po_id}>
                  {opt.printer_option_value}
                </MenuItem>
              ))}
            </Select>
            {isChanged("color") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          {/* Orientation */}
          <FormControl component="fieldset" size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Orientation
            </FormLabel>
            <RadioGroup
              row
              value={printForm.orientation}
              onChange={(e) => handlePrintChange("orientation", e.target.value)}
            >
              {optionsByCategory["CAT003"]?.map((opt) => (
                <FormControlLabel
                  key={opt.po_id}
                  value={opt.po_id}
                  control={<Radio disabled={!isEditing} />}
                  label={opt.printer_option_value}
                />
              ))}
            </RadioGroup>
            {isChanged("orientation") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          {/* Scale */}
          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Scale
            </FormLabel>
            <Select
              disabled={!isEditing}
              value={printForm.scale}
              onChange={(e) => handlePrintChange("scale", e.target.value)}
            >
              {optionsByCategory["CAT004"]?.map((opt) => (
                <MenuItem key={opt.po_id} value={opt.po_id}>
                  {opt.printer_option_value}
                </MenuItem>
              ))}
            </Select>
            {isChanged("scale") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          {/* Stapler */}
          <FormControl component="fieldset" size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Stapler
            </FormLabel>
            <RadioGroup
              row
              value={printForm.stapler}
              onChange={(e) => handlePrintChange("stapler", e.target.value)}
            >
              <FormControlLabel
                value="YES"
                control={<Radio disabled={!isEditing} />}
                label="Yes"
              />
              <FormControlLabel
                value="NO"
                control={<Radio disabled={!isEditing} />}
                label="No"
              />
            </RadioGroup>
            {isChanged("stapler") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          {/* Staple Position */}
          <FormControl
            fullWidth
            size="small"
            disabled={printForm.stapler !== "YES"}
          >
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Staple Position
            </FormLabel>
            <Select
              disabled={!isEditing}
              value={printForm.staplePosition || ""}
              onChange={(e) =>
                handlePrintChange("staplePosition", e.target.value)
              }
            >
              {optionsByCategory["CAT006"]?.map((opt) => (
                <MenuItem key={opt.po_id} value={opt.po_id}>
                  {opt.printer_option_value}
                </MenuItem>
              ))}
            </Select>
            {isChanged("staplePosition") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth size="small">
            <FormLabel sx={{ fontSize: "14px", color: "black", mb: 1 }}>
              Page per sheet
            </FormLabel>
            <Select
              disabled={!isEditing}
              value={printForm.pages_per_sheet}
              onChange={(e) =>
                handlePrintChange("pages_per_sheet", e.target.value || "")
              }
              size="small"
            >
              {optionsByCategory["CAT008"]?.map((opt: any) => (
                <MenuItem key={opt.po_id} value={opt.po_id}>
                  {opt.printer_option_value}
                </MenuItem>
              ))}
            </Select>
            {isChanged("staplePosition") && (
              <Typography color="#e80909ff" fontSize="12px" mt={0.5}>
                Edited by tutor
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>

      {/* ---------- Total Request Qty ---------- */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          marginRight: "20px",
        }}
      >
        <Typography fontSize={"14px"} fontWeight={600}>
          Total Request Quantity: {totalQty}
        </Typography>
      </Box>

      {/* ---------- Remarks ---------- */}
      <Box sx={{ mb: 3, p: 2 }}>
        <Typography mb={1} fontSize="14px">
          Tutor Remark
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={tutorRemarks}
          disabled
          InputProps={{
            sx: {
              fontSize: "14px",
              maxWidth: "600px",
              backgroundColor: "#f5f5f5",
              borderRadius: "6px",
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0,0,0,0.87)", // keep readable text color
              },
            },
          }}
        />

        <Typography
          sx={{ marginTop: "20px", fontSize: "14px" }}
          variant="body2"
          gutterBottom
        >
          Admin Remarks *
        </Typography>
        <TextField
          label="Admin Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          multiline
          InputProps={{
            sx: {
              fontSize: "14px",
              maxWidth: "600px",
              backgroundColor: "#f5f5f5",
              borderRadius: "6px",
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "rgba(0,0,0,0.87)", // keep readable text color
              },
            },
          }}
          rows={3}
          required={isSaved} // 🔹 Required only after save
          error={isSaved && !remarks.trim()} // 🔹 Show error only if required and empty
          helperText={
            isSaved && !remarks.trim()
              ? "Remarks are required after saving."
              : ""
          }
        />
      </Box>

      {/* ---------- Action Buttons ---------- */}
      {data?.data?.approval_status === "pending" && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {isEditing && (
            <Typography color="error" fontSize="13px" fontWeight={500} mb={1}>
              Warning: Click "Save" to apply print settings before approving.
            </Typography>
          )}

          {!isEditing && isSaved && !remarks.trim() && (
            <Typography color="error" fontSize="13px" fontWeight={500} mb={1}>
              Warning: Admin remarks for tutor modified.
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              sx={{
                color: "#0a0a0aff",
                borderColor: "none",
                fontSize: "14px",
                textTransform: "none",
                borderRadius: "20px",
              }}
              onClick={() => handleDecision("rejected")}
              disabled={isEditing} // disable when printer flow active
            >
              Reject
            </Button>

            <Button
              variant="contained"
              sx={{
                fontSize: "14px",
                color: "#ffffff",
                bgcolor: "#009DFF",
                borderRadius: "20px",
                textTransform: "none",
                "&:hover": {
                  opacity: 0.8,
                  bgcolor: "#009DFF",
                },
              }}
              onClick={() => handleDecision("approved")}
              disabled={isEditing || (isSaved && !remarks.trim())}
            >
              Approve
            </Button>
          </Box>
        </Box>
      )}
      <Dialog
        open={openReprintDialog}
        onClose={() => setOpenReprintDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: "600" }}>
          Reprint – Update Quantity
        </DialogTitle>

        <DialogContent>
          <CartCommanTable
            columns={[
              { key: "subject", title: "Subject" },
              { key: "classGroup", title: "Class/Group" },
              {
                key: "File",
                title: "File",
              },
              { key: "lastUploadDate", title: "Last Upload Date" },
              {
                key: "quantity",
                title: "Qty",
                render: (_value, record) => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      backgroundColor: "#f3f3f3",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleQtyChange(record.cart_id, -1)}
                    >
                      <RemoveIcon
                        fontSize="small"
                        sx={{ backgroundColor: "#009DDF", color: "#ffffff" }}
                      />
                    </IconButton>

                    <Typography
                      fontSize="14px"
                      fontWeight="600"
                      sx={{ minwidth: "20px" }}
                    >
                      {record.quantity}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => handleQtyChange(record.cart_id, +1)}
                    >
                      <AddIcon
                        fontSize="small"
                        sx={{ backgroundColor: "#009DDF", color: "#ffffff" }}
                      />
                    </IconButton>
                  </Box>
                ),
                align: "center",
              },
            ]}
            data={reprintCart}
            loading={false}
            pageType="modal"
          />
          {isSubmitDisabled && (
            <Typography
              color="error"
              fontSize="13px"
              sx={{ mt: 1, textAlign: "right", mr: 1 }}
            >
              Please select at least 1 quantity!
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenReprintDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleReprintSubmit}
              disabled={isSubmitDisabled}
              sx={{
                fontSize: "14px",
                color: "#ffffff",
                ml: 2,
                bgcolor: isSubmitDisabled ? "#bdbdbd" : "#009DFF",
                cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                borderRadius: "12px",
                textTransform: "none",
                "&:hover": {
                  opacity: 0.8,
                  bgcolor: "#009DFF",
                },
              }}
            >
              {/* {reprintLoading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Submit"
              )} */}
              submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={dialogOpen || reprintSuccess}
        message={dialogMsg}
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {
          setDialogOpen(false);
          navigate("/booklet_request");
        }}
      />
    </Box>
  );
};

export default BookletRequestDetail;
